import { PublicHeader } from "@/components/layout/PublicHeader";
import { PublicFooter } from "@/components/layout/PublicFooter";
import { usePageMeta } from "@/hooks/use-page-meta";

export default function Privacy() {
  usePageMeta({
    title: "Privacy Policy – Buukd",
    description:
      "How Buukd collects, uses, and protects your data. Privacy policy for our appointment booking, membership, and catering platform.",
    path: "/privacy",
  });

  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      
      <main className="flex-1 pt-24 pb-12">
        <div className="container-app max-w-4xl">
          <h1 className="text-4xl font-bold text-foreground mb-2">
            Privacy Policy
          </h1>
          <p className="text-muted-foreground mb-8">
            Last Updated: {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
          </p>

          <div className="prose prose-slate max-w-none space-y-8 text-foreground">
            <section>
              <p className="text-muted-foreground">
                This Privacy Policy describes how Buukd ("we", "us", or "our") collects, uses, and protects your personal information when you use our booking and business management platform (the "Platform" or "Services"). By using our Services, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">1. INFORMATION WE COLLECT</h2>
              
              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">1.1 Information You Provide</h3>
              <p className="text-muted-foreground">
                We collect information that you provide directly to us, including:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                <li><strong className="text-foreground">Account Information:</strong> Name, email address, phone number, business name, and password when you create an account</li>
                <li><strong className="text-foreground">Business Information:</strong> Business details, locations, services, staff members, and operational data</li>
                <li><strong className="text-foreground">Customer Data:</strong> Information about your customers, including names, contact details, appointment history, membership details, and order information</li>
                <li><strong className="text-foreground">Payment Information:</strong> Billing details and payment method information (processed securely through third-party payment processors)</li>
                <li><strong className="text-foreground">Communication Data:</strong> Messages, support requests, and feedback you send to us</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">1.2 Information We Collect Automatically</h3>
              <p className="text-muted-foreground">
                When you use our Services, we automatically collect certain information, including:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                <li><strong className="text-foreground">Usage Data:</strong> How you interact with the Platform, features used, pages visited, and time spent</li>
                <li><strong className="text-foreground">Device Information:</strong> IP address, browser type, operating system, device identifiers, and mobile network information</li>
                <li><strong className="text-foreground">Location Data:</strong> General location information based on IP address (if you enable location services)</li>
                <li><strong className="text-foreground">Cookies and Tracking:</strong> We use cookies and similar technologies to enhance your experience and analyze usage patterns</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">1.3 Information from Third Parties</h3>
              <p className="text-muted-foreground">
                We may receive information from third-party services you integrate with, such as:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                <li>Payment processors (e.g., Paystack) for transaction information</li>
                <li>Google Maps for location services</li>
                <li>Analytics providers for usage statistics</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">2. HOW WE USE YOUR INFORMATION</h2>
              <p className="text-muted-foreground">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                <li>Provide, maintain, and improve our Services</li>
                <li>Process transactions and send related information</li>
                <li>Send you technical notices, updates, security alerts, and support messages</li>
                <li>Respond to your comments, questions, and requests</li>
                <li>Monitor and analyze trends, usage, and activities</li>
                <li>Detect, prevent, and address technical issues and fraudulent activity</li>
                <li>Personalize your experience and provide content tailored to your interests</li>
                <li>Comply with legal obligations and enforce our Terms & Conditions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">3. DATA SHARING AND DISCLOSURE</h2>
              <p className="text-muted-foreground">
                We do not sell your personal information. We may share your information only in the following circumstances:
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">3.1 Service Providers</h3>
              <p className="text-muted-foreground">
                We may share information with trusted third-party service providers who perform services on our behalf, such as:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                <li>Payment processing (Paystack)</li>
                <li>Cloud hosting and infrastructure (Supabase)</li>
                <li>Email delivery services</li>
                <li>Analytics and monitoring tools</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                These providers are contractually obligated to protect your information and use it only for the purposes we specify.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">3.2 Legal Requirements</h3>
              <p className="text-muted-foreground">
                We may disclose your information if required by law or in response to valid requests by public authorities (e.g., court orders, subpoenas).
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">3.3 Business Transfers</h3>
              <p className="text-muted-foreground">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">3.4 With Your Consent</h3>
              <p className="text-muted-foreground">
                We may share your information with your explicit consent or at your direction.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">4. DATA SECURITY</h2>
              <p className="text-muted-foreground">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication mechanisms</li>
                <li>Secure data centers and infrastructure</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">5. DATA RETENTION</h2>
              <p className="text-muted-foreground">
                We retain your personal information for as long as necessary to:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                <li>Provide our Services to you</li>
                <li>Comply with legal obligations</li>
                <li>Resolve disputes and enforce our agreements</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                When you cancel your account, we will retain your data for 30 days to allow for data export. After this period, your data will be permanently deleted unless we are required to retain it for legal purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">6. YOUR RIGHTS AND CHOICES</h2>
              <p className="text-muted-foreground">
                Depending on your location, you may have certain rights regarding your personal information:
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">6.1 Access and Portability</h3>
              <p className="text-muted-foreground">
                You can access and download your data through your account settings or by contacting us.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">6.2 Correction</h3>
              <p className="text-muted-foreground">
                You can update your account information at any time through your account settings.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">6.3 Deletion</h3>
              <p className="text-muted-foreground">
                You can request deletion of your account and data by contacting us. We will process your request within 30 days, subject to legal retention requirements.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">6.4 Opt-Out</h3>
              <p className="text-muted-foreground">
                You can opt out of marketing communications by clicking the unsubscribe link in our emails or adjusting your notification preferences in your account settings.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">6.5 Cookies</h3>
              <p className="text-muted-foreground">
                Most web browsers allow you to control cookies through their settings. However, disabling cookies may limit your ability to use certain features of our Services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">7. INTERNATIONAL DATA TRANSFERS</h2>
              <p className="text-muted-foreground">
                Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. We ensure appropriate safeguards are in place, including:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                <li>Standard Contractual Clauses approved by relevant data protection authorities</li>
                <li>Compliance with applicable data protection regulations (GDPR, CCPA, Ghana's Data Protection Act)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">8. CHILDREN'S PRIVACY</h2>
              <p className="text-muted-foreground">
                Our Services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">9. CHANGES TO THIS PRIVACY POLICY</h2>
              <p className="text-muted-foreground">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                <li>Posting the new Privacy Policy on this page</li>
                <li>Sending you an email notification</li>
                <li>Displaying a notice in the Platform</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                The "Last Updated" date at the top of this page indicates when the policy was last revised.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">10. CONTACT US</h2>
              <p className="text-muted-foreground">
                If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us through the contact information available on our website.
              </p>
            </section>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
